import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';

/**
 * The app's dropdown: a panel that opens as a FIXED overlay, plus the rows inside it.
 *
 * Why not `position: absolute` next to the trigger: a menu inside a scrolling, clipping or transformed
 * ancestor is rendered, styled and unreachable — an ancestor clips it, or something later in the document
 * paints over it. The app's own contract (and the browser suite) says a menu is a fixed overlay whose items
 * are what sits at their own centre. A portal with fixed coordinates is the only way to be sure.
 *
 * Placement: below the trigger, right-aligned when `align="right"`, flipped above it if there is no room
 * below, and clamped into the viewport so it can never open half off-screen.
 *
 * What the component OWNS — the part a per-page copy always forgets:
 *   - `role="menu"` on the panel and `aria-labelledby` back to the trigger, with `aria-haspopup="menu"`,
 *     `aria-expanded` and `aria-controls` on the trigger through `useMenu()`;
 *   - Arrow/Home/End roving focus among the rows, first-letter type-ahead, Escape that closes AND returns
 *     focus to the trigger, Tab that closes without trapping;
 *   - focus onto the first (or last) row only when the menu was opened with the keyboard — a pointer open
 *     leaves focus on the trigger, which is what native menus do.
 */
export function Menu({
  id,
  open,
  onClose,
  anchorRef,
  align = 'left',
  minWidth,
  className,
  shadow,
  portal = true,
  keepMounted = false,
  labelledBy,
  focusOnOpen = 'none',
  children,
}: {
  id: string;
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  align?: 'left' | 'right';
  minWidth?: number;
  className?: string;
  /** The panel's shadow, for a menu whose EJS twin carries its own (`0 8px 30px var(--scrim)` on the pane). */
  shadow?: string;
  /**
   * Render into <body> (the default) or in place. The pane menu renders IN PLACE, as the EJS one does: it is
   * addressed as `tr.detail-row .pane-menu` by the page's own CSS and by the browser suite, and a portalled
   * copy is not that element. Position is fixed either way, so an in-place menu still escapes clipping.
   */
  portal?: boolean;
  /**
   * Keep the panel in the DOM while closed, hidden — what the EJS markup does (`display: none`, rows present).
   * A pane menu's rows are measured by the page's own scripts and by the suite before the first open.
   */
  keepMounted?: boolean;
  /** The trigger's id, when the trigger did not come from `useMenu()`. */
  labelledBy?: string;
  /** Where focus goes as the menu opens: `none` for a pointer open, `first`/`last` for a keyboard one. */
  focusOnOpen?: 'none' | 'first' | 'last';
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Measure once the menu is in the DOM (its own size decides whether it fits below).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const a = anchorRef.current;
    if (!a) return;
    const place = () => {
      const r = a.getBoundingClientRect();
      const box = ref.current?.getBoundingClientRect();
      const w = box?.width ?? minWidth ?? 160;
      const h = box?.height ?? 0;
      // 4px below the trigger, the gap PFOverlay uses on every EJS menu (`const MARGIN = 8, GAP = 4`).
      const gap = 4;
      const margin = 8;
      let top = r.bottom + gap;
      if (h && top + h > window.innerHeight - margin) {
        const above = r.top - gap - h;
        top = above >= margin ? above : Math.max(margin, window.innerHeight - margin - h);
      }
      let left = align === 'right' ? r.right - w : r.left;
      left = Math.min(Math.max(margin, left), window.innerWidth - margin - w);
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, align, anchorRef, minWidth]);

  // Focus lands on a row only for a keyboard open; the pointer user keeps the trigger focused.
  useEffect(() => {
    if (!open || focusOnOpen === 'none') return;
    const items = menuItems(ref.current);
    const target = focusOnOpen === 'last' ? items[items.length - 1] : items[0];
    if (target) target.focus();
  }, [open, focusOnOpen]);

  // Escape closes from anywhere: with focus on a row (keyboard open) and with focus still on the trigger
  // (pointer open). It also returns focus to the trigger, so the reader is not dropped on <body>.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      anchorRef.current?.focus();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, anchorRef]);

  // Arrows on the trigger while the menu is open: Enter opened it, so ArrowDown walks in rather than doing
  // nothing (the panel is portalled elsewhere, so its own keydown handler never sees this event).
  useEffect(() => {
    if (!open) return;
    const a = anchorRef.current;
    if (!a) return;
    const onAnchorKey = (e: KeyboardEvent) => {
      if (document.activeElement !== a) return;
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const items = menuItems(ref.current);
      const target = e.key === 'ArrowDown' ? items[0] : items[items.length - 1];
      if (target) target.focus();
    };
    a.addEventListener('keydown', onAnchorKey);
    return () => a.removeEventListener('keydown', onAnchorKey);
  }, [open, anchorRef]);

  // A pointer outside closes it. The trigger is excluded: its own click toggles, and closing here first
  // would make that toggle open it again.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, onClose, anchorRef]);

  if (!open && !keepMounted) return null;

  const closeAndReturnFocus = () => {
    // Focus the trigger BEFORE the panel unmounts: closing first drops focus onto <body> for a frame, and a
    // keyboard reader has to start over from the top of the document.
    anchorRef.current?.focus();
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = menuItems(ref.current);
    if (!items.length) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        items[(at + 1) % items.length].focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        items[(at - 1 + items.length) % items.length].focus();
        break;
      case 'Home':
        e.preventDefault();
        items[0].focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1].focus();
        break;
      case 'Tab':
        // No trap: the menu closes and focus leaves from the trigger.
        closeAndReturnFocus();
        break;
      case ' ':
        // A focused row that is not a native control (the pane menu's label) still activates.
        if (at >= 0 && !(document.activeElement instanceof HTMLButtonElement)) {
          e.preventDefault();
          (document.activeElement as HTMLElement).click();
        }
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) typeAhead(items, e.key);
    }
  };

  const panel = (
    <div
      id={id}
      data-menu
      ref={ref}
      className={className}
      role="menu"
      aria-labelledby={labelledBy || anchorRef.current?.id || undefined}
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed',
        display: open ? undefined : 'none',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        background: 'var(--panel)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        minWidth,
        zIndex: 200,
        outline: 'none',
        // The EJS menu's own shadow (`0 4px 16px var(--scrim)`), not a second, softer one; a page may pass its
        // own where its EJS twin differs (the pane menu uses `0 8px 30px`).
        boxShadow: shadow ?? '0 4px 16px var(--scrim)',
      }}
    >
      <MenuCloseContext.Provider value={onClose}>{children}</MenuCloseContext.Provider>
    </div>
  );

  return portal ? createPortal(panel, document.body) : panel;
}

/** The panel's focusable rows, in reading order: disabled and hidden ones are skipped. */
export function menuItems(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return [...root.querySelectorAll<HTMLElement>('[role^="menuitem"]')]
    .filter((el) => el.getAttribute('aria-disabled') !== 'true')
    .filter((el) => el.offsetParent !== null || el === document.activeElement);
}

// First-letter jumps, 600ms per letter: a menu of six rows is reached by "d" sooner than by four arrows.
let typeBuffer = '';
let typeAt = 0;
function typeAhead(items: HTMLElement[], ch: string) {
  const now = Date.now();
  typeBuffer = now - typeAt > 600 ? ch.toLowerCase() : typeBuffer + ch.toLowerCase();
  typeAt = now;
  const from = Math.max(-1, items.indexOf(document.activeElement as HTMLElement));
  const ordered = [...items.slice(from + 1), ...items.slice(0, from + 1)];
  const hit = ordered.find((el) => (el.textContent || '').trim().toLowerCase().startsWith(typeBuffer));
  if (hit) hit.focus();
}

/**
 * The trigger's half of the contract: its id, `aria-haspopup`, `aria-expanded`, `aria-controls`, a click that
 * toggles, and ArrowDown/ArrowUp that open onto the first/last row. Spread `triggerProps` on the button and
 * `menuProps` on <Menu>.
 */
export function useMenu(ids: { menu?: string; trigger?: string } = {}) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [focusOnOpen, setFocusOnOpen] = useState<'none' | 'first' | 'last'>('none');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  // Named ids where the page (or its CSS and its tests) already knows the menu by one: `#user-menu`,
  // `#list-menu`. The generated pair is for a menu that has no id of its own.
  const triggerId = ids.trigger || `${uid}-trigger`;
  const menuId = ids.menu || `${uid}-menu`;

  const openWith = useCallback((where: 'first' | 'last') => {
    setFocusOnOpen(where);
    setOpen(true);
  }, []);

  const triggerProps = {
    id: triggerId,
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open,
    'aria-controls': menuId,
    onClick: () => { setFocusOnOpen('none'); setOpen((v) => !v); },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); openWith('first'); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); openWith('last'); }
      else if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); }
    },
  };

  const menuProps = {
    id: menuId,
    open,
    onClose: () => setOpen(false),
    anchorRef: triggerRef,
    labelledBy: triggerId,
    focusOnOpen,
  };

  return { open, setOpen, openWith, triggerRef, triggerProps, menuProps, menuId, triggerId };
}

const MenuCloseContext = createContext<(() => void) | null>(null);

/**
 * One row of a menu. Appearance is the caller's (`className` — `.um-item`, `.pane-menu-item`, or the default
 * `.menu-item`); behaviour is the component's: the right role, `aria-checked` for a checkable row, disabled
 * rows out of the tab order, and a click that acts once and then closes the menu.
 */
export function MenuItem({
  children,
  className = 'menu-item',
  icon,
  href,
  onSelect,
  type = 'button',
  form,
  checked,
  disabled = false,
  danger = false,
  closeOnSelect,
  ...rest
}: {
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
  href?: string;
  onSelect?: () => void;
  type?: 'button' | 'submit';
  form?: string;
  checked?: boolean;
  disabled?: boolean;
  danger?: boolean;
  closeOnSelect?: boolean;
} & Omit<React.HTMLAttributes<HTMLElement>, 'onSelect' | 'children'>) {
  const close = useContext(MenuCloseContext) ?? (() => {});
  const role = checked === undefined ? 'menuitem' : 'menuitemcheckbox';
  const cls = [className, danger ? 'is-danger' : ''].filter(Boolean).join(' ');
  const act = (e: React.MouseEvent) => {
    if (disabled) { e.preventDefault(); return; }
    onSelect?.();
    // A submit row defaults to NOT closing: unmounting its form between mousedown and click swallows the
    // submit, which is exactly how "Log out" stopped logging out the moment the menu came from the component.
    if (closeOnSelect ?? type !== 'submit') close();
  };

  // A checkable row draws its own check column: `aria-checked` is what a screen reader hears, and this is what
  // the reader sees. (The pane menu's row keeps its native checkbox instead, which is its form value.)
  const check = checked === undefined ? null : (
    <span aria-hidden="true" className="menu-check">{checked ? '✓' : ''}</span>
  );
  if (href) {
    return (
      <a {...rest} role={role} aria-checked={checked} aria-disabled={disabled || undefined}
        className={cls} tabIndex={disabled ? -1 : 0}
        href={disabled ? undefined : href} onClick={act}>
        {check}{icon}{children}
      </a>
    );
  }
  return (
    <button {...rest} type={type} form={form} role={role} aria-checked={checked}
      aria-disabled={disabled || undefined} className={cls} disabled={disabled}
      tabIndex={disabled ? -1 : 0} onClick={act}>
      {check}{icon}{children}
    </button>
  );
}

/** A divider between groups of rows (`role="separator"`, which a menu's structure expects). */
export function MenuSeparator({ style }: { style?: React.CSSProperties }) {
  return <div role="separator" style={{ borderTop: '1px solid var(--border)', margin: '4px 10px', ...style }} />;
}

/** A non-interactive heading inside a menu — a group label, never focusable. */
export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div role="presentation" className="menu-label" style={{
      padding: '6px 12px 2px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em',
      color: 'var(--muted)',
    }}>
      {children}
    </div>
  );
}
